import React, { createContext, useContext, useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import jwtService from "../services/jwtService";
import authService from "../services/authService";

// Create the context
const AuthContext = createContext();

/**
 * Provider component for authentication context
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const history = useHistory();

  // Check authentication on component mount
  useEffect(() => {
    const checkAuth = () => {

      try {
        // Prevent unnecessary checks if we just logged out
        const justLoggedOut = sessionStorage.getItem('justLoggedOut');
        if (justLoggedOut) {
          sessionStorage.removeItem('justLoggedOut');
          setUser(null);
          setLoading(false);
          return;
        }

        // The context's only job is to sync its state with the token.
        // The routing components in App.js will handle the redirect.
        if (authService.isAuthenticated()) {
          const userInfo = jwtService.getUserInfo();
          setUser(userInfo);
        } else {
          // Clear user state if token is invalid
          setUser(null);
        }
      } catch (err) {
        console.error("Auth check error:", err);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  /**
   * Handle user login
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<Object>} - Login result
   */
  const login = async (email, password) => {
    setLoading(true);
    setError(null);

    try {
      const response = await authService.signIn(email, password);

      // Check if the response indicates a successful login
      if (response && response.token) {
        const userInfo = jwtService.getUserInfo();
        setUser(userInfo);

        // Redirect any authenticated user to the app; only fall back to sign-in if token is missing
        if (userInfo && userInfo.roles && userInfo.roles.length > 0) {
          history.push("/press-digest");
        } else {
          history.push("/authentication/sign-in");
        }

        return { success: true, data: response };
      } else {
        // Clear any existing user data on failed login
        setUser(null);
        return { success: false, error: "Login failed. Please check your credentials." };
      }
    } catch (err) {
      // Clear any existing user data on error
      setUser(null);
      setError(err.response?.data?.message || "Login failed. Please check your credentials.");
      return { success: false, error: err.response?.data?.message || err.message };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle user registration
   * @param {Object} userData - User registration data
   * @returns {Promise<Object>} - Registration result
   */
  const register = async (userData) => {
    setLoading(true);
    setError(null);

    try {
      const response = await authService.signUp(userData);
      return { success: true, data: response };
    } catch (err) {
      setError(err.response?.data?.message || "Registration failed. Please try again.");
      return { success: false, error: err.response?.data?.message || err.message };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle user logout
   */
  const logout = async () => {
    setLoading(true);

    try {
      await authService.logout();
      setUser(null);
      history.push("/authentication/sign-in");
    } finally {
      setLoading(false);
    }
  };

  const checkTokenValidity = () => {
    return jwtService.isAuthenticated();
  };

  // Context value
  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user && checkTokenValidity(),
    login,
    register,
    logout,
    hasRole: (role) => user && user.roles && user.roles.includes(role),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Custom hook to use the auth context
 * @returns {Object} - Auth context value
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
